<template>
  <section
    name="patient"
    class="patient-add__grid"
    ref="formPatient">
    <input-text
      class="patient-add__grid__header"
      placeholder="№ медкарты"
      v-mask="cardNumberMask"
      v-bind:violations="medicalCardNumberInputErrors"
      v-model="medicalCardNumber"
      v-on:blur="onBlur"
      title="№ Медицинской карты"
    />
    <div class="patient-add__grid__body">
      <input-text
        title="Фамилия"
        placeholder="Фамилия"
        v-on:blur="onBlur"
        v-bind:violations="lastNameInputErrors"
        v-letter-capital
        v-model.trim="lastName" />
      <input-text
        title="Имя"
        placeholder="Имя"
        v-on:blur="onBlur"
        v-bind:violations="firstNameInputErrors"
        v-letter-capital
        v-model="firstName" />
      <input-text
        title="Отчество"
        placeholder="Отчество"
        v-on:blur="onBlur"
        v-bind:violations="patronymicInputErrors"
        v-letter-capital
        v-model="patronymic" />
      <input-date
        label="Дата рождения"
        type="date"
        v-on:blur="onBlur"
        v-bind:max="dateNow"
        v-bind:violations="birthDateInputErrors"
        v-model="birthDate" />
    </div>
    <textarea-length-limitation
      class="patient-add__grid__footer"
      placeholder="Примечание"
      rows="1"
      v-bind:max-length="100"
      v-bind:violations="noteInputErrors"
      v-letter-capital
      v-model="note" />
  </section>
</template>

<script>
import InputDate from '@/components/InputDate.vue';
import InputText from '@/components/InputText.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import dateMixin from '@/mixins/dateMixin';
import focusMixin from '@/mixins/focusMixin';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Форма заполнения личных данных пациента.
 */
export default {
  components: { TextareaLengthLimitation, InputText, InputDate },
  created() {
    this.$nextTick(() => {
      this.focusIn(this.$refs.formPatient);
    });
  },
  computed: {
    dateNow: ({ formatDateToISO }) => formatDateToISO(new Date()),
    birthDateInputErrors: resolveInputErrors('birth_date'),
    firstNameInputErrors: resolveInputErrors('first_name'),
    lastNameInputErrors: resolveInputErrors('last_name'),
    medicalCardNumberInputErrors: resolveInputErrors('medical_card_number'),
    noteInputErrors: resolveInputErrors('note'),
    patronymicInputErrors: resolveInputErrors('patronymic'),
    patient: ({
      birthDate, firstName, lastName, medicalCardNumber, note, patronymic,
    }) => ({
      birthDate,
      firstName,
      lastName,
      medicalCardNumber,
      note,
      patronymic,
    }),
  },
  data() {
    return {
      /**
       * @description Дата рождения пациента.
       * @type {string}
       */
      birthDate: this.value.birthDate,
      /**
       * @description Имя пациента.
       * @type {string}
       */
      firstName: this.value.firstName,
      /**
       * @description Фамилия пациента.
         @type {string}
       */
      lastName: this.value.lastName,
      /**
       * @description Номер мед карты.
       * @type {string}
       */
      medicalCardNumber: this.value.medicalCardNumber,
      /**
       * @description Примечание.
       * @type {string}
       */
      note: this.value.note,
      /**
       * @description Отчество пациента.
       * @type {string}
       */
      patronymic: this.value.patronymic,
    };
  },
  methods: {
    onBlur() {
      this.$emit('blur', this.patient);
    },
    /**
     * @description Маска мед. карты.
     * @returns {function}
     */
    cardNumberMask(value) {
      return [value.replace(/[^0-9-—/a-zA-Zа-яА-ЯёЁ]/, '').substring(0, 20)];
    },
  },
  mixins: [focusMixin, dateMixin],
  props: {
    /**
     * @description Данные пациента.
     * @type {any}
     */
    value: { type: Object, required: true },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    violationCauses: { type: Object, required: true },
  },
  watch: {
    patient: {
      handler(value) {
        this.$emit('input', value);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.patient-add {
  &__grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    margin: 0 0 20px 0;
    grid-row-gap: 20px;
    grid-column-gap: 12px;
    &__header {
      grid-row-start: 1;
      grid-column-start: 1;
      grid-column-end: 3;
    }
    &__body {
      grid-column-start: 1;
      grid-column-end: 6;
      grid-row-start: 2;
      grid-row-end: 3;
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      align-items: flex-start;
      grid-gap: 12px;
    }
    &__footer {
      grid-row-start: 3;
      grid-column-start: 1;
      grid-column-end: 6;
      margin: 15px 0 0 0;
    }
  }
}
@media screen and (max-width: 960px) {
  .patient-add {
    &__grid {
      grid-template-columns: repeat(3, 1fr);
      &__header {
        grid-column-start: 1;
        grid-column-end: 3;
      }
      &__body {
        grid-column-start: 1;
        grid-column-end: 4;
        grid-template-columns: repeat(3, 1fr);
      }
      &__footer {
        grid-column-start: 1;
        grid-column-end: 4;
      }
    }
  }
}
@media screen and (max-width: 600px) {
  .patient-add {
    &__grid {
      grid-template-columns: repeat(2, 1fr);
      &__header {
        grid-column-start: 1;
        grid-column-end: 2;
      }
      &__body {
        grid-column-start: 1;
        grid-column-end: 3;
        grid-template-columns: repeat(2, 1fr);
      }
      &__footer {
        grid-column-start: 1;
        grid-column-end: 3;
      }
    }
  }
}
@media screen and (max-width: 600px) {
  .patient-add {
    &__grid {
      grid-template-columns: 1fr;
      &__header {
        grid-column-start: 1;
        grid-column-end: 2;
      }
      &__body {
        grid-column-start: 1;
        grid-column-end: 2;
        grid-template-columns: 1fr;
      }
      &__footer {
        grid-column-start: 1;
        grid-column-end: 2;
      }
    }
  }
}
</style>
